import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { radii, textSize } from 'design/tokens';

function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

/** A 400px drawer on the right, above the shell bar and below the rail. */
export const SidePanel: React.FC<{ onClose: () => void; children: React.ReactNode; label: string }> = ({ onClose, children, label }) => {
  useEscape(onClose);
  return createPortal(
    <div className="st-root-portal">
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 64, background: 'rgba(16,16,20,.18)' }} />
      <aside
        aria-label={label}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 65,
          width: 400,
          maxWidth: '100%',
          background: 'var(--st-card)',
          boxShadow: '-12px 0 40px var(--st-edge3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          color: 'var(--st-ink)',
        }}
      >
        {children}
      </aside>
    </div>,
    portalRoot(),
  );
};

export const Modal: React.FC<{
  onClose: () => void;
  width: number;
  scrim?: number;
  children: React.ReactNode;
  label: string;
  maxHeight?: string;
}> = ({ onClose, width, scrim = 0.28, children, label, maxHeight }) => {
  useEscape(onClose);
  return createPortal(
    <div className="st-root-portal">
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: `rgba(16,16,20,${scrim})` }} />
      <div
        role="dialog"
        aria-label={label}
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%,-50%)',
          zIndex: 81,
          width,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: maxHeight ?? '86vh',
          background: 'var(--st-card)',
          color: 'var(--st-ink)',
          borderRadius: radii.xl,
          boxShadow: '0 24px 60px var(--st-edge3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>,
    portalRoot(),
  );
};

/**
 * Portalled overlays have to stay inside `.st-root`, where the design tokens
 * live, or every `var(--st-*)` in them resolves to nothing.
 */
function portalRoot(): Element {
  return document.querySelector('.st-root') ?? document.body;
}

export const CloseX: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    aria-label="Close"
    onClick={onClick}
    style={{
      font: 'inherit',
      fontSize: textSize.medium,
      background: 'var(--st-fill)',
      border: 0,
      color: 'var(--st-ink3)',
      padding: '6px 10px',
      borderRadius: radii.lg,
      cursor: 'pointer',
      flex: '0 0 auto',
    }}
  >
    ✕
  </button>
);
